import React, { useState } from 'react';
import { Lightbulb, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Tip {
  title: string;
  description: string;
  icon: React.ReactNode;
  link: string;
  linkText: string;
}

export default function TipsSection() {
  const [currentTip, setCurrentTip] = useState(0);
  const navigate = useNavigate();
  
  const tips: Tip[] = [
    {
      title: "Conecte suas redes sociais",
      description: "Conecte suas contas para receber ideias personalizadas para cada plataforma.",
      icon: <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#db2777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
      </div>,
      link: "/dashboard/configuracoes?tab=redes-sociais",
      linkText: "Saiba mais"
    },
    {
      title: "Crie seu primeiro plano",
      description: "Gere um plano personalizado respondendo algumas perguntas sobre seu conteúdo.",
      icon: <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
        <Lightbulb className="h-5 w-5 text-indigo-600" />
      </div>,
      link: "/dashboard/ideias",
      linkText: "Saiba mais"
    },
    {
      title: "Acompanhe seu desempenho",
      description: "Adicione dados de desempenho para visualizar métricas e insights valiosos.",
      icon: <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
      </div>,
      link: "/dashboard/analises",
      linkText: "Saiba mais"
    },
    {
      title: "Organize seu calendário",
      description: "Planeje suas publicações com antecedência usando o calendário de conteúdo.",
      icon: <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
      </div>,
      link: "/dashboard/calendario",
      linkText: "Saiba mais"
    },
    {
      title: "Descubra seu nicho ideal",
      description: "Use nossa ferramenta de descoberta de nicho para encontrar seu diferencial.",
      icon: <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
      </div>,
      link: "/dashboard/ideias",
      linkText: "Saiba mais"
    }
  ];
  
  const nextTip = () => {
    setCurrentTip((prev) => (prev + 1) % tips.length);
  };
  
  const prevTip = () => {
    setCurrentTip((prev) => (prev - 1 + tips.length) % tips.length);
  };
  
  const handleNavigate = (path: string) => {
    navigate(path);
  };
  
  const tip = tips[currentTip];
  
  return (
    <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Lightbulb className="h-5 w-5 mr-2 text-amber-500" />
        Dicas para Começar
      </h3>
      
      <div className="relative">
        <div className="flex items-center p-4 bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg">
          <div className="mr-4 flex-shrink-0">
            {tip.icon}
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{tip.title}</h4>
            <p className="text-sm text-gray-600 mt-1">{tip.description}</p>
            <button 
              onClick={() => handleNavigate(tip.link)}
              className="inline-flex items-center text-amber-600 hover:text-amber-700 text-sm font-medium mt-2"
            >
              {tip.linkText}
              <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </button>
          </div>
        </div>
        
        <div className="absolute inset-y-0 left-0 flex items-center">
          <button 
            onClick={prevTip}
            className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md -ml-4 hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        
        <div className="absolute inset-y-0 right-0 flex items-center">
          <button 
            onClick={nextTip}
            className="w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-md -mr-4 hover:bg-gray-50 transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>
      
      <div className="flex justify-center mt-4">
        {tips.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentTip(index)}
            className={`w-2 h-2 mx-1 rounded-full ${
              index === currentTip ? 'bg-amber-500' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}